import { useTranslation } from 'react-i18next';
import { Icon, Text, Spinner, Button, TextProps, IconNames } from '@sendible/design-system/src';
import { EVENTS, publish } from '@sendible/event-manager';
import { useBackgroundUploaderContext } from '@sendible/frontendv2/src/components/BackgroundUploader/context';
import { fileSizeLimits } from '@sendible/frontendv2/src/models/medias/consts';
import { MediaFileType } from '@sendible/frontendv2/src/models/medias/types';
import { ContentBar, FileInfo, Container, NameContainer, StatusIcon } from './index.styles';

export const UploadingFile = ({ id, index, uploadProgress, mediaId, status, mediaType, name, errorCode }: UploadingFileProps) => {
  const { t } = useTranslation('compose_box');
  const { canCancelUpload } = useBackgroundUploaderContext();

  let icon: IconNames;

  switch (mediaType) {
    case MediaFileType.Video:
      icon = 'video';
      break;
    case MediaFileType.Image:
      icon = 'image';
      break;
    case MediaFileType.Document:
      icon = 'file';
      break;
    case MediaFileType.Unspecified:
      icon = 'file';
      break;
    default:
      icon = 'file';
  }

  return (
    <Container
      key={id}
      status={status}
      $uploadProgress={uploadProgress ?? 0}
      data-testid={`uploading-${name}`}
      initial={{ x: '-25%', opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: '25%', opacity: 0 }}
      transition={{ delay: index ? index * 0.0125 : 0, ease: 'easeInOut' }}
    >
      <ContentBar>
        <Icon
          customClass="type-icon"
          color={status === 'Ready' ? 'brandDark' : 'brandDarkAlpha30'}
          name={icon}
        />
        <FileInfo>
          <NameContainer>
            <Text
              align="left"
              color={(status === 'Ready' || status === 'Failed' ? 'brandDark' : 'brandDarkAlpha70') as TextProps['color']}
              maxLines={1}
              variation="body_14"
              title={name.length > 27 ? name : ''}
            >
              {name}
            </Text>
            {status === 'Failed' && errorCode && (
              <Text
                className="sub-message"
                align="left"
                color={'semanticDangerShade20' as TextProps['color']}
                maxLines={1}
                variation="body_12"
                testId={`upload-error-${name}`}
                title={t(errorCode) || ''}
              >
                {t(errorCode, {
                  fileType: `${mediaType?.toLowerCase()}s`,
                  sizeLimit: mediaType && fileSizeLimits[mediaType],
                })}
              </Text>
            )}
          </NameContainer>
          {status !== 'Ready' && status !== 'Failed' && (
            <StatusIcon>
              <Spinner
                testId={`upload-spinner-${name}`}
                className="spinner-icon"
                size="xs"
              />
              {canCancelUpload(id) && (
                <Button
                  icon="close"
                  data-testid={`cancel-${name}`}
                  className="cancel-button"
                  onClick={() => publish(EVENTS.UPLOAD_MEDIA_CANCELLED, mediaId)}
                />
              )}
            </StatusIcon>
          )}
          {status === 'Ready' && (
            <StatusIcon>
              <Icon
                data-testid={`uploader-success-${name}`}
                customClass="status-icon"
                color="semanticSuccessShade50"
                name="check"
                strokeWidth={1.7}
              />
            </StatusIcon>
          )}
          {status === 'Failed' && (
            <StatusIcon>
              <Icon
                data-testid="uploader-error"
                customClass="status-icon"
                color="semanticDanger"
                name="danger"
                strokeWidth={1.7}
              />
            </StatusIcon>
          )}
        </FileInfo>
      </ContentBar>
    </Container>
  );
};
